import {
    AuthTokenData,
    PriceLevelFilterState, PriceListUtil,
    SelectableInfo,
    StructuredPricelevel,
    SeatmapClientDataViewModel,
    SeatmapState, TicketTypeItem, TicketTypeMaximumAmountProvider, TokenMetaData
} from "@/domain/ticket-selection";
import { Hold, PriceLevel, Promotion, PromotionHold } from "webshop-api";
import {
    BlockInfoDTO,
    GeneralAdmission,
    PriceCategory,
    Seat,
    SeatInfoDTO
} from "@eventim/seatmap-client";
import { ObjectUtils } from "webshop-modules";
import { ArrayHelper } from "webshop-modules/helper/Array.helper";
import jwtDecode from "jwt-decode";

export class SeatmapUtils {

    public static async selectionByPriceLevels(
        state: SeatmapState,
        clientData: SeatmapClientDataViewModel,
        priceLevels: PriceLevel[],
        promotionState: Promotion,
        maxTickets: number,
        maxTicketsOrder: number,
    ): Promise<StructuredPricelevel[]> {
        const structuredPricelevels: StructuredPricelevel[] = [];
        const currentSelection = clientData.selectedTickets;

        if (!currentSelection.length) {
            return structuredPricelevels;
        }

        await Promise.all(currentSelection.map(async (item) => {
            const blockInfo: BlockInfoDTO = await clientData.getBlockInfoFor(item);
            const priceCategory = clientData.getPriceCategoryFor(item);
            if (!priceCategory || !item.selectable.priceCategory) {
                return;
            }

            const pricelevel = <PriceLevel>priceLevels.find(pl => pl.priceLevelId === priceCategory.id);
            const seatInfo: SeatInfoDTO | undefined = blockInfo.seatInfos.find(seat => seat.id === item.selectable.id);

            let ticketTypes: TicketTypeItem[] = [];

            if (seatInfo) {
                const structuredSelection = state.structuredSelection.find(
                    selection => selection.selectionInfo.id === seatInfo.id
                );

                if (structuredSelection) {
                    ticketTypes = structuredSelection.selectionInfo.ticketTypes || [];
                }
            }

            const itemInfo: SelectableInfo = {
                id: item.selectable.id,
                priceCategoryId: item.selectable.priceCategory.id,
                priceCategoryName: item.selectable.priceCategory.name,
                color: priceCategory.color,
                currency: pricelevel.currency ?? '',
                maxTickets: this.getMaxTicketsForPriceCategory(
                    promotionState,
                    pricelevel,
                    state.maxTickets,
                    state.maxTicketsOrder,
                    item.selectable,
                ),
                description: pricelevel.description ?? '',
                entranceName: seatInfo!.entrance,
                name: pricelevel.text && pricelevel.text.length <= 45 ? pricelevel.text : priceCategory.name,
                blockName: blockInfo.name,
                ticketTypes: this.getTicketTypeStateListFor(
                    promotionState,
                    pricelevel,
                    maxTickets,
                    maxTicketsOrder,
                    item.selectable,
                    ticketTypes
                ),
                isGeneralAdmission: false,
            }

            if (seatInfo) {
                itemInfo.amount = item.amount;
                itemInfo.seat = seatInfo.seatNumber;
                itemInfo.row = seatInfo.row;
                itemInfo.table = seatInfo.table ?? '';
                itemInfo.isGeneralAdmission = seatInfo.ga;
            }

            if (!itemInfo.ticketTypes.some(item => item.active)) {
                itemInfo.ticketTypes = itemInfo.ticketTypes
                    .sort((a, b) => a.number - b.number)
                    .map((item, index) => {
                        if (index === 0) {
                            item.active = true;
                            return item;
                        }
                        return item;
                    });
            }

            if (itemInfo.amount) {
                const pricelevelTickets = this.duplicateTicketsByAmount(itemInfo);

                pricelevelTickets.forEach(ticket => {
                    structuredPricelevels.push({ id: ticket.priceCategoryId, selectionInfo: ticket });
                });

                return;
            }


            const designatedPriceLevel = structuredPricelevels.find(pl => pl.id === itemInfo.priceCategoryId);

            designatedPriceLevel ?
                designatedPriceLevel.selectionInfo = itemInfo
                : structuredPricelevels.push({id: itemInfo.priceCategoryId, selectionInfo: itemInfo});
        }));

        return structuredPricelevels;
    }

    public static getTicketTypeStateListFor(
        promotionState: Promotion,
        priceLevel: PriceLevel,
        maxTickets: number,
        maxTicketsOrder: number,
        seatInfo: Seat | GeneralAdmission,
        ticketTypes: TicketTypeItem[]
    ): TicketTypeItem[] {
        const promotionId = promotionState !== null ? promotionState.id : 0;
        if (!priceLevel.promotionHolds) {
            return [];
        }

        const promotionHold = priceLevel.promotionHolds.find(item => item.id === promotionId);
        if (!promotionHold) {
            return [];
        }

        const hold = this.getPriceLevelHoldFrom(seatInfo, promotionHold);
        if (!hold) {
            return [];
        }

        return hold.ticketTypes!.map((ticketType, index) => {
            const ticketTypeState = ticketTypes.find(type => type.priceId == ticketType.priceId);
            const active = ticketTypeState ? ticketTypeState.active : false
            return {
                active,
                priceId: ticketType.priceId!,
                min: ticketType.minTicketsPerOrder ? ticketType.minTicketsPerOrder : 0,
                max: TicketTypeMaximumAmountProvider.getMaximumFor(
                    ticketType,
                    [maxTickets, maxTicketsOrder].reduce(ArrayHelper.ToLowestValue, Number.MAX_SAFE_INTEGER)
                ),
                number: ticketType.number ? ticketType.number : index,
                step: ticketType.onlyInGroupsOfPerOrder ? ticketType.onlyInGroupsOfPerOrder : 1,
                name: ticketType.text!,
                price: ticketType.price!
            };
        });
    }

    public static buildPriceLevelFilterStateFor(
        priceCategories: PriceCategory[],
        priceLevels: PriceLevel[],
        promotion: Promotion | null
    ): PriceLevelFilterState[] {
        if (!priceCategories || !priceLevels) {
            return [];
        }

        return priceCategories
            .filter(category => this.getPriceLevelFor(category, priceLevels, promotion) !== undefined)
            .map(category => {
                const priceLevel = this.getPriceLevelFor(category, priceLevels, promotion);

                if (!priceLevel) {
                    throw new Error('No price level with id: ' + category.id);
                }

                return {
                    color: category.color,
                    id: priceLevel!.priceLevelId!,
                    name: priceLevel!.text!.length <= 45 ? priceLevel!.text! : category.name,
                    order: priceLevel!.number!,
                    startPrice: PriceListUtil.getStartPriceFor(priceLevel, promotion),
                    textColor: category.textColor
                };
            })
            .sort((a, b) => a.order - b.order);
    }

    public static getPriceLevelFor(priceCategory: PriceCategory, priceLevels: PriceLevel[], promotion: Promotion | null): PriceLevel | undefined {
        const priceLevel = priceLevels.find(level => level.priceLevelId === priceCategory.id);

        if (!priceLevel) {
            return undefined;
        }

        if (
            promotion &&
            promotion.id &&
            priceLevel.promotionHolds
        ) {
            return priceLevel.promotionHolds.some(promotionHold => promotionHold.id === promotion.id) ? priceLevel : undefined;
        }

        if (
            priceLevel.promotionHolds &&
            priceLevel.promotionHolds.some(promotionHold => promotionHold.id === 0)
        ) {
            return priceLevel;
        }

        return undefined;
    }

    public static getMaxTicketsForPriceCategory(
        promotion: Promotion,
        priceLevel: PriceLevel,
        maxTickets: number,
        maxTicketsOrder: number,
        seatInfo: Seat | GeneralAdmission,
    ): number {
        const promotionId = promotion !== null ? promotion.id : 0;
        if (!priceLevel.promotionHolds) {
            return 0;
        }

        const promotionHold = priceLevel.promotionHolds.find(item => item.id === promotionId);
        if (!promotionHold) {
            return 0;
        }

        const hold = this.getPriceLevelHoldFrom(seatInfo, promotionHold);
        if (!hold) {
            return 0;
        }

        const max = [maxTickets, maxTicketsOrder];

        max.push(hold.totalFreeSeats ? hold.totalFreeSeats : 0);

        if (promotion !== null && promotion.maxTicketsPerEvent) {
            max.push(promotion.maxTicketsPerEvent);
        }

        if (promotion !== null && promotion.maxTicketsPerOrder) {
            max.push(promotion.maxTicketsPerOrder);
        }

        if (
            promotion !== null &&
            promotion.totalNumbersOfTickets !== undefined &&
            promotion.soldNumbersOfTickets !== undefined
        ) {
            max.push(promotion.totalNumbersOfTickets - promotion.soldNumbersOfTickets);
        }

        return max.reduce(ArrayHelper.ToLowestValue, Number.MAX_SAFE_INTEGER);
    }

    public static getPriceLevelsWithDefaultPromotionHoldFrom(pricelevels: PriceLevel[]): PriceLevel[] {
        return pricelevels.filter(priceLevel => {
            if (!priceLevel.promotionHolds || !priceLevel.promotionHolds.length) {
                return false;
            }

            return priceLevel.promotionHolds.some(promotionHold => promotionHold.id === 0);
        })
    }

    public static getPriceLevelsWithHoldsFor(promotionId: number, priceLevels: PriceLevel[]): PriceLevel[] {
        return priceLevels.filter(priceLevel => {
            if (!priceLevel.promotionHolds || !priceLevel.promotionHolds.length) {
                return false;
            }

            return priceLevel.promotionHolds.some(promotionHold => promotionHold.id === promotionId);
        })
    }

    public static isAvailabilityLockedBehindPromotions(priceLevels: PriceLevel[], promotion: Promotion | null): boolean {
        if (promotion !== null) {
            return false;
        }

        const availablePriceCategories = SeatmapUtils.getPriceLevelsWithDefaultPromotionHoldFrom(priceLevels);

        return availablePriceCategories.length === 0;
    }

    public static mapToTokenDataFrom(token: string): AuthTokenData {
        const tokenMetaData = jwtDecode<TokenMetaData>(token);

        return {
            token,
            expireDate: tokenMetaData && tokenMetaData.exp ? tokenMetaData.exp * 1000 : 0,
        }
    }

    private static duplicateTicketsByAmount(ticket: SelectableInfo): SelectableInfo[] {
        const amount = ticket.amount ?? 1;
        const tickets: SelectableInfo[] = [];

        for (let i = 0; i < amount; i++) {
            tickets.push(ObjectUtils.clone({...ticket, selectionDisplayPosition: i}));
        }

        return tickets;
    }

    private static getPriceLevelHoldFrom(seatInfo: Seat | GeneralAdmission, promotionHold: PromotionHold): Hold | undefined {
        let hold: Hold | undefined = undefined;
        if (seatInfo && seatInfo.holds) {
            hold = promotionHold.holds!.find(item => (seatInfo.holds ?? []).some(hold => hold.id === item.number));
        }

        return hold ?? promotionHold.holds!.find(item => item.number === 0);
    }

    public static getSeatParamsFor(key: string, value: number) {
        return encodeURIComponent(`seats[${key}]`) + `=${value}`;
    }

    public static getStandParamsFor(key: string, value: number) {
        return encodeURIComponent(`stands[${key}]`) + `=${value}`;
    }

    public static async getAdditionalInfoFor(seat: Seat, client: SeatmapClientDataViewModel) {
        const block: BlockInfoDTO = await client.getBlockInfoFor({ selectable: seat, amount: 1 });

        return block.seatInfos.find(seatData => seatData.id === seat.id);
    }
}
